import { memo } from 'react';
import { NextSeo } from 'next-seo';
import Head from 'next/head';

import {
  MR_SCHEMA,
  OPEN_GRAPH_IMAGE,
  ORGANIZATION_SCHEMA,
} from '@mais-retorno-front/constants/mais-retorno-description';
import { AmpHtmlLink, ItemListSchema, NextPrevLink, WebPageSchema } from '.';
import { useSEOListTemplate } from '@mais-retorno-front/hooks/seo';

interface TemplateListSEOSchemasProps {
  pageTitle: string;
  pageDescription: string;
  basePath: string;
  isAmp: boolean;
  currentPage: number;
  lastPage: number;
  listItems: { url: string; name: string }[];
  hasAmp?: boolean;
}

/**
 * Use this component to apply SEO components in list templates
 * @returns Defaults SEO components
 */
export const TemplateListSEOSchemas = memo<TemplateListSEOSchemasProps>(
  ({
    pageTitle,
    pageDescription,
    basePath,
    listItems,
    currentPage = 1,
    lastPage = null,
    isAmp,
    hasAmp = true,
  }) => {
    const {
      ampPathVersionWithPagination,
      canonicalPathWithPagination,
      pageTitleWithPagination,
    } = useSEOListTemplate({
      currentPage,
      basePath,
      pageTitle,
    });

    const currentPagePath = isAmp
      ? ampPathVersionWithPagination
      : canonicalPathWithPagination;

    return (
      <>
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: ORGANIZATION_SCHEMA }}
          />
        </Head>

        <WebPageSchema
          name={pageTitleWithPagination}
          url={canonicalPathWithPagination}
          description={pageDescription}
        />

        <ItemListSchema
          name={pageTitleWithPagination}
          url={currentPagePath}
          description={pageDescription}
          listItems={listItems}
        />

        {!isAmp && hasAmp && (
          <AmpHtmlLink ampHref={ampPathVersionWithPagination} />
        )}

        <NextPrevLink
          baseUrl={basePath}
          currentPage={currentPage}
          lastPage={lastPage}
        />

        <NextSeo
          title={pageTitleWithPagination}
          description={pageDescription}
          canonical={canonicalPathWithPagination}
          openGraph={{
            type: 'website',
            url: currentPagePath,
            title: pageTitleWithPagination,
            description: pageDescription,
            site_name: MR_SCHEMA.organizationName,
            locale: MR_SCHEMA.organizationLocale,
            images: [OPEN_GRAPH_IMAGE],
          }}
        />
      </>
    );
  }
);
